import { reactive } from "vue";

import type backendOld from "~/api/backendOld";
import { isDefined } from "~/shared/common";
import { DocKind, FolderKind, FolderSort, IconKind, PageKind } from "~/shared/enums";
import type { Doc, DocUpdate, Folder, FolderUpdate, Task, TaskDocRelationship } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import {
  deepCopy,
  getFolderLink,
  getNextTitleInSequence,
  makeDuid,
  makeEmptyLexicalState,
  randomSample,
} from "~/utils/common";
import { makeFolderComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

type DocsByFolderOptions = { includeTrashed?: boolean; includeDraft?: boolean; order?: FolderSort | null };

export type State = {
  _duidsToDocs: Map<string, Doc>;
  // TODO incremental remove sorted docs like we did tasks and implement placeholder data for docs like we did for tasks https://github.com/its-dart/dart/pull/2815#discussion_r2249499788
  _sortedDocDuids: string[];
  _duidsToFolders: Map<string, Folder>;
  _sortedFolderDuids: string[];
};

export const state: State = {
  _duidsToDocs: new Map(),
  _sortedDocDuids: [],
  _duidsToFolders: new Map(),
  _sortedFolderDuids: [],
};

export type Getters = {
  getFolderList: (options?: { includeHidden?: boolean }) => Folder[];
  getFolderByDuid: (duid: string) => Folder | undefined;
  getFoldersBySpaceDuidOrdered: (spaceDuid: string, options?: { includeHidden?: boolean }) => Folder[];
  defaultFolder: Folder;
  reportsFolder: (spaceDuid: string) => Folder;
  getDocByDuid: (duid: string) => Promise<Doc | undefined>;
  getDocByDuidIfLoaded: (duid: string) => Doc | undefined;
  getDocsByDuidsOrdered: (duids: string[]) => Promise<Doc[]>;
  getDocsByDuidsOrderedIfLoaded: (duids: string[]) => Doc[];
  getDocsByFolderDuidOrdered: (folderDuid: string, options?: DocsByFolderOptions) => Promise<Doc[]>;
  getAllLoadedDocsForUiOnly: () => Doc[];
  getLoadedDocsByFolderDuidOrdered: (folderDuid: string, options?: DocsByFolderOptions) => Doc[];
  getDocDuidsRelatedToTask: (task: Task) => string[];
  getDocsRelatedToTaskOrdered: (task: Task) => Promise<Doc[]>;
  getDocsRelatedToTaskOrderedIfLoaded: (task: Task) => Doc[];
  getTaskDuidsRelatedToDoc: (doc: Doc) => string[];
  getTasksRelatedToDocOrdered: (doc: Doc) => Promise<Task[]>;
  getTasksRelatedToDocOrderedIfLoaded: (doc: Doc) => Task[];
};

export type Actions = {
  createFolder: (spaceDuid: string, order: string, options?: { awaitBackend?: boolean }) => Promise<Folder>;
  updateFolder: (update: FolderUpdate, options?: { awaitBackend?: boolean }) => Promise<Folder | undefined>;
  replicateFolder: (
    folder: Folder,
    order: string,
    partialFolder?: Partial<Folder>,
    options?: { awaitBackend?: boolean; maintainTitle?: boolean }
  ) => Promise<Folder | undefined>;
  deleteFolder: (folder: Folder, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setFolders: (folders: Folder[]) => void;
  $createOrUpdateFolder: (folder: Folder) => void;
  $deleteFolder: (folder: Folder) => void;
  loadDocs: (...args: Parameters<typeof backendOld.entityList.doc>) => ReturnType<typeof backendOld.entityList.doc>;
  loadPaginatedDocsForFolderDuid: (folderDuid: string, order: FolderSort) => Promise<number>;
  createDoc: (
    title: string,
    folderDuid: string,
    order: string,
    partialDoc?: Partial<Doc>,
    options?: { relatedTaskDuids?: string[]; awaitBackend?: boolean }
  ) => Promise<Doc>;
  updateDocs: (
    updates: DocUpdate[],
    options?: { noBackend?: boolean; awaitBackend?: boolean }
  ) => Promise<Doc[] | undefined>;
  addDocSubscribers: (
    docDuid: string,
    subscriberDuids: string[],
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  removeDocSubscribers: (
    docDuid: string,
    subscriberDuids: string[],
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  replicateDoc: (
    doc: Doc,
    order: string,
    partialDoc?: Partial<Doc>,
    options?: { awaitBackend?: boolean; maintainTitle?: boolean }
  ) => Promise<Doc | undefined>;
  trashDoc: (doc: Doc, options?: { awaitBackend?: boolean }) => Promise<void>;
  $updateDocEditorsAndSubscribersNoBackend: (
    doc: Doc,
    userDuids: string[]
  ) => { newEditorDuids: string[]; newSubscriberDuids: string[] };
  $createOrUpdateDoc: (doc: Doc, extras?: { hardUpdate?: boolean }) => void;
  $deleteDoc: (doc: Doc) => void;
  createTaskDocRelationship: (
    taskDuid: string,
    docDuid: string,
    options?: { awaitBackend?: boolean }
  ) => Promise<TaskDocRelationship | undefined>;
  deleteTaskDocRelationship: (taskDuid: string, docDuid: string, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createTaskDocRelationship: (taskDocRelationship: TaskDocRelationship) => Promise<void>;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getFolderList() {
    return (options = {}) => {
      const { includeHidden = false } = options;
      return this._sortedFolderDuids
        .map((e) => this._duidsToFolders.get(e))
        .filter(isDefined)
        .filter((e) => includeHidden || e.kind !== FolderKind.REPORTS);
    };
  },
  getFolderByDuid() {
    return (duid) => this._duidsToFolders.get(duid);
  },
  getFoldersBySpaceDuidOrdered() {
    return (spaceDuid, options = {}) => this.getFolderList(options).filter((e) => e.spaceDuid === spaceDuid);
  },
  defaultFolder() {
    const folder = this.getFolderList().filter((e) => e.kind === FolderKind.DEFAULT);
    if (folder.length !== 1) {
      throw new Error("Expected exactly one default folder");
    }
    return folder[0];
  },
  reportsFolder() {
    return (spaceDuid) => {
      const res = this.getFolderList({ includeHidden: true }).find(
        (e) => e.spaceDuid === spaceDuid && e.kind === FolderKind.REPORTS
      );
      if (!res) {
        throw new Error("No reports folder found");
      }
      return res;
    };
  },
  getDocByDuid() {
    return async (duid) => {
      const doc = this.getDocByDuidIfLoaded(duid);
      if (doc) {
        return doc;
      }

      if (this.$isEntityDuidBlacklisted(duid)) {
        return undefined;
      }

      const docFetched = await this.$batchers.entityListManager.getEntityByDuid("docs", duid);
      if (!docFetched) {
        this.$blacklistEntityDuid("docs", duid);
        return undefined;
      }

      this.$createOrUpdateDoc(docFetched);
      return docFetched;
    };
  },
  getDocByDuidIfLoaded() {
    return (duid) => this._duidsToDocs.get(duid);
  },
  getDocsByDuidsOrdered() {
    return async (duids) => {
      const docs = await Promise.all(duids.map((e) => this.getDocByDuid(e)));
      return docs.filter(isDefined).sort(this.$getDocSortingFn(FolderSort.ORDER));
    };
  },
  getDocsByDuidsOrderedIfLoaded() {
    return (duids) =>
      duids
        .map((e) => this.getDocByDuidIfLoaded(e))
        .filter(isDefined)
        .sort(this.$getDocSortingFn(FolderSort.ORDER));
  },
  getDocsByFolderDuidOrdered() {
    return async (folderDuid, options) => {
      const { data } = await this.$backendOld.docs.getByFolder(folderDuid);
      data.items.forEach((doc: Doc) => {
        if (this.getDocByDuidIfLoaded(doc.duid) || doc.inTrash) {
          return;
        }
        this.$createOrUpdateDoc(doc);
      });

      return this.getLoadedDocsByFolderDuidOrdered(folderDuid, options);
    };
  },
  getAllLoadedDocsForUiOnly() {
    return () => [...this._duidsToDocs.values()];
  },
  getLoadedDocsByFolderDuidOrdered() {
    return (folderDuid, options) => {
      const { includeTrashed = false, includeDraft = false, order = FolderSort.ORDER } = options ?? {};
      const paginationState = this.$getPaginationState(folderDuid, order);

      const placeholderData = this._sortedDocDuids;
      const docs = (paginationState ? [...paginationState.duids.values()] : placeholderData)
        .map((e) => this.getDocByDuidIfLoaded(e))
        .filter(isDefined)
        .filter(
          (e) => e.folderDuid === folderDuid && (includeTrashed || !e.inTrash) && (includeDraft || !e.drafterDuid)
        );
      return paginationState && order ? docs.sort(this.$getDocSortingFn(order)) : docs;
    };
  },
  getDocDuidsRelatedToTask() {
    return (task) => task.docRelationships.map((e) => e.docDuid);
  },
  getDocsRelatedToTaskOrdered() {
    return (task) => this.getDocsByDuidsOrdered(this.getDocDuidsRelatedToTask(task));
  },
  getDocsRelatedToTaskOrderedIfLoaded() {
    return (task) => this.getDocsByDuidsOrderedIfLoaded(this.getDocDuidsRelatedToTask(task));
  },
  getTaskDuidsRelatedToDoc() {
    return (doc) => doc.taskRelationships.map((e) => e.taskDuid);
  },
  getTasksRelatedToDocOrdered() {
    return (doc) => this.getTasksByDuidsOrdered(this.getTaskDuidsRelatedToDoc(doc));
  },
  getTasksRelatedToDocOrderedIfLoaded() {
    return (doc) => this.getTasksByDuidsOrderedIfLoaded(this.getTaskDuidsRelatedToDoc(doc));
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  /* FOLDERS */
  async createFolder(spaceDuid, order, options = {}) {
    const { awaitBackend = false } = options;
    const appStore = this.$useAppStore();

    const newFolder: Folder = {
      pageKind: PageKind.FOLDER,
      duid: makeDuid(),
      spaceDuid,
      kind: FolderKind.OTHER,
      order,
      title: "",
      description: "",
      iconKind: IconKind.NONE,
      iconNameOrEmoji: randomSample(appStore.pageIconOptions)[0].name,
      colorHex: makeRandomColorHex(),
    };
    this.$createOrUpdateFolder(newFolder);

    const backendAction = this.$backend.folder.create(newFolder);
    if (awaitBackend) {
      await backendAction;
    }
    return newFolder;
  },
  async updateFolder(update, options = {}) {
    const { awaitBackend = false } = options;
    const folder = this.getFolderByDuid(update.duid);
    if (!folder) {
      return undefined;
    }

    Object.assign(folder, update);
    updateInSorted(
      this._duidsToFolders,
      this._sortedFolderDuids,
      folder.duid,
      makeFolderComparator(this.getSpaceByDuid)
    );

    const backendAction = this.$backend.folder.update(update);
    if (awaitBackend) {
      await backendAction;
    }

    return folder;
  },
  async replicateFolder(folder, order, partialFolder, options = {}) {
    const { awaitBackend = false, maintainTitle = false } = options;
    const newFolder: Folder = {
      ...deepCopy(folder),
      duid: makeDuid(),
      kind: FolderKind.OTHER,
      order,
      ...partialFolder,
    };
    if (!maintainTitle && partialFolder?.title === undefined) {
      newFolder.title = getNextTitleInSequence(
        folder.title,
        this.getFoldersBySpaceDuidOrdered(newFolder.spaceDuid, { includeHidden: true }).map((e) => e.title)
      );
    }

    this.$createOrUpdateFolder(newFolder);

    // TODO transactionify these two calls
    const backendAction = (async () => {
      await this.$backend.folder.create(newFolder);
      await this.$backendOld.folders.replicate(folder.duid, { targetDuid: newFolder.duid });
    })();

    if (awaitBackend) {
      await backendAction;
    }

    return newFolder;
  },
  async deleteFolder(folder, options = {}) {
    const { awaitBackend = false } = options;
    const defaultFolderDuid = this.defaultFolder.duid;
    if (!defaultFolderDuid) {
      return;
    }

    // TODO transactionify
    const updates = this.getLoadedDocsByFolderDuidOrdered(folder.duid, { includeTrashed: true }).map((e) => {
      const res: DocUpdate = {
        duid: e.duid,
        folderDuid: defaultFolderDuid,
      };
      if (!e.inTrash) {
        res.inTrash = true;
      }
      return res;
    });

    this.$deleteFolder(folder);

    this.updateDocs(updates);

    const backendAction = this.$backend.folder.delete(folder.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setFolders(folders) {
    setObjectMapAndSorted(
      this._duidsToFolders,
      this._sortedFolderDuids,
      folders.map((e) => ({ ...e, pageKind: PageKind.FOLDER }) satisfies Folder),
      makeFolderComparator(this.getSpaceByDuid)
    );
  },
  async $createOrUpdateFolder(folder) {
    const currentFolder = this.getFolderByDuid(folder.duid);

    // TODO remove this check when we are filtering out spurious updates on BE of WS
    const space = this.getSpaceByDuid(folder.spaceDuid);
    if (!space) {
      return;
    }
    if (!(space.accessibleByTeam || space.accessibleByUserDuids.includes(this.$useUserStore().duid))) {
      if (currentFolder) {
        this.$deleteFolder(folder);
      }
      return;
    }

    // eslint-disable-next-line no-param-reassign
    folder.pageKind = PageKind.FOLDER;

    if (!currentFolder) {
      this._duidsToFolders.set(folder.duid, folder);
    } else {
      Object.assign(currentFolder, folder);
    }
    updateInSorted(
      this._duidsToFolders,
      this._sortedFolderDuids,
      folder.duid,
      makeFolderComparator(this.getSpaceByDuid)
    );

    /* Create */
    // TODO shouldn't rely on client to get the docs separately, this should probably come over WS
    if (!currentFolder) {
      await this.getDocsByFolderDuidOrdered(folder.duid);
    }
  },
  $deleteFolder(folder) {
    const deletingCurrentPage = folder.duid === this.$useAppStore().currentPage?.duid;

    this.$deletePaginationStates(folder.duid);
    this._duidsToFolders.delete(folder.duid);
    removeFromSorted(this._sortedFolderDuids, folder.duid);

    // TODO shouldn't rely on client to delete these, this should probably come over WS; also this is bad because it can be bad for the normal delete flow
    this.getLoadedDocsByFolderDuidOrdered(folder.duid, { includeTrashed: true, includeDraft: true }).forEach((duid) =>
      this.$deleteDoc(duid)
    );

    if (deletingCurrentPage) {
      this.$routerUtils.goHome();
    }
  },
  /* DOCS */
  async loadDocs(...args) {
    const response = await this.$backendOld.entityList.doc(...args);
    response.data.results.forEach((doc) => {
      if (this.getDocByDuidIfLoaded(doc.duid)) {
        return;
      }
      this.$createOrUpdateDoc(doc);
    });
    return response;
  },
  async loadPaginatedDocsForFolderDuid(folderDuid, order) {
    const folder = this.getFolderByDuid(folderDuid);
    if (!folder) {
      return 0;
    }

    let paginationState = this.$getPaginationState(folderDuid, order);
    const { data } = await this.loadDocs({ folderDuid, order }, paginationState?.duids.size ?? 0);
    if (!paginationState) {
      paginationState = this.$createPaginationState(folderDuid, order, this.$getDocSortingFn(order));
    }
    this.$addEntitiesToPaginationState(folderDuid, order, data.results, data.count);

    return data.count;
  },
  async createDoc(title, folderDuid, order, partialDoc, options = {}) {
    const { relatedTaskDuids = [], awaitBackend = false } = options;

    const appStore = this.$useAppStore();
    const userStore = this.$useUserStore();

    const now = new Date().toISOString();

    const newDoc: Doc = {
      pageKind: PageKind.DOC,
      duid: makeDuid(),
      kind: DocKind.DOC,
      createdAt: now,
      createdByDuid: userStore.duid,
      updatedAt: now,
      updatedByDuid: userStore.duid,
      drafterDuid: null,
      inTrash: false,
      folderDuid,
      reportKind: null,
      order,
      title,
      text: makeEmptyLexicalState(),
      recommendationDuid: null,
      editorDuids: [userStore.duid],
      subscriberDuids: [userStore.duid],
      iconKind: IconKind.NONE,
      iconNameOrEmoji: randomSample(appStore.pageIconOptions)[0].name,
      colorHex: makeRandomColorHex(),
      taskRelationships: [],
      ...partialDoc,
    };
    this.$createOrUpdateDoc(newDoc);
    appStore.addRecentDoc(newDoc);

    this.$addEntitiesToPaginationStates(folderDuid, [newDoc]);

    const relationships = relatedTaskDuids.map((taskDuid) => {
      const taskDocRelationshipCreate = { duid: makeDuid(), taskDuid, docDuid: newDoc.duid };
      this.$createTaskDocRelationship(taskDocRelationshipCreate);
      return taskDocRelationshipCreate;
    });

    const backendAction = this.$backend.doc.create(newDoc, relationships);
    if (awaitBackend) {
      await backendAction;
    }
    return newDoc;
  },
  async updateDocs(updates, options = {}) {
    const appStore = this.$useAppStore();

    const { noBackend = false, awaitBackend = false } = options;
    if (updates.length === 0) {
      return undefined;
    }

    const updaterDuid = this.$useUserStore().duid;

    const listAdds: DocUpdate[] = [];
    const updatedDocs: Doc[] = [];

    updates.forEach((update) => {
      const doc = this.getDocByDuidIfLoaded(update.duid);
      if (!doc) {
        return;
      }

      if (update.folderDuid !== undefined && doc.folderDuid !== update.folderDuid) {
        this.$removeEntitiesFromPaginationStates([doc]);
      }

      appStore.addRecentDoc(doc);

      if (doc.recommendationDuid) {
        // eslint-disable-next-line no-param-reassign
        update.recommendationDuid = null;
      }

      const { newEditorDuids, newSubscriberDuids } = this.$updateDocEditorsAndSubscribersNoBackend(doc, [updaterDuid]);
      if (newEditorDuids.length > 0 || newSubscriberDuids.length > 0) {
        const listAdd: DocUpdate = { duid: doc.duid };
        if (newEditorDuids.length > 0) {
          listAdd.editorDuids = newEditorDuids;
        }
        if (newSubscriberDuids.length > 0) {
          listAdd.subscriberDuids = newSubscriberDuids;
        }
        listAdds.push(listAdd);
      }

      Object.assign(doc, update);
      if (!("updatedAt" in update)) {
        doc.updatedAt = new Date().toISOString();
      }
      updatedDocs.push(doc);

      this.$addEntitiesToPaginationStates(doc.folderDuid, [doc]);
      updateInSorted(this._duidsToDocs, this._sortedDocDuids, doc.duid, this.$getDocSortingFn(FolderSort.ORDER));
    });

    if (noBackend) {
      return updatedDocs;
    }

    const backendAction = this.$backend.doc.updateUpdateListAddMany(updates, listAdds);
    if (awaitBackend) {
      await backendAction;
    }
    return updatedDocs;
  },
  async addDocSubscribers(docDuid, subscriberDuids, options = {}) {
    const { awaitBackend = false } = options;
    const doc = await this.getDocByDuid(docDuid);
    if (!doc) {
      return;
    }

    doc.subscriberDuids = [...new Set([...doc.subscriberDuids, ...subscriberDuids])];
    doc.updatedAt = new Date().toISOString();
    updateInSorted(this._duidsToDocs, this._sortedDocDuids, docDuid, this.$getDocSortingFn(FolderSort.ORDER));

    const backendAction = this.$backend.doc.updateListAdd({ duid: docDuid, subscriberDuids });
    if (awaitBackend) {
      await backendAction;
    }
  },
  async removeDocSubscribers(docDuid, subscriberDuids, options = {}) {
    const { awaitBackend = false } = options;
    const doc = await this.getDocByDuid(docDuid);
    if (!doc) {
      return;
    }

    doc.subscriberDuids = doc.subscriberDuids.filter((e) => !subscriberDuids.includes(e));
    doc.updatedAt = new Date().toISOString();
    updateInSorted(this._duidsToDocs, this._sortedDocDuids, docDuid, this.$getDocSortingFn(FolderSort.ORDER));

    const backendAction = this.$backend.doc.updateListRemove({ duid: docDuid, subscriberDuids });
    if (awaitBackend) {
      await backendAction;
    }
  },
  async replicateDoc(doc, order, partialDoc, options = {}) {
    const { awaitBackend = false, maintainTitle = false } = options;
    const folder = this.getFolderByDuid(doc.folderDuid);
    if (!folder) {
      return undefined;
    }

    const now = new Date().toISOString();
    const newDoc: Doc = {
      ...deepCopy(doc),
      duid: makeDuid(),
      createdAt: now,
      updatedAt: now,
      order,
      editorDuids: [this.$useUserStore().duid],
      ...partialDoc,
    };
    if (!maintainTitle && partialDoc?.title === undefined) {
      newDoc.title = getNextTitleInSequence(
        doc.title,
        this.getLoadedDocsByFolderDuidOrdered(newDoc.folderDuid).map((e) => e.title)
      );
    }

    this.$createOrUpdateDoc(newDoc);

    const taskDocRelationshipCreates = (await this.getTasksRelatedToDocOrdered(doc)).map((task) => {
      const taskDocRelationshipCreate = { duid: makeDuid(), taskDuid: task.duid, docDuid: newDoc.duid };
      this.$createTaskDocRelationship(taskDocRelationshipCreate);
      return taskDocRelationshipCreate;
    });

    const backendAction = this.$backend.doc.create(newDoc, taskDocRelationshipCreates);
    if (awaitBackend) {
      await backendAction;
    }

    return newDoc;
  },
  async trashDoc(doc, options = {}) {
    const { awaitBackend = false } = options;

    this.$removeEntitiesFromPaginationStates([doc]);
    this.$useAppStore().removeRecentDoc(doc.duid);

    const folder = this.getFolderByDuid(doc.folderDuid);
    if (!folder) {
      this.$routerUtils.goHome();
      return;
    }
    await this.$router.replace(getFolderLink(folder, this.getSpaceByDuid));

    // eslint-disable-next-line no-param-reassign
    doc.inTrash = true;
    // eslint-disable-next-line no-param-reassign
    doc.updatedAt = new Date().toISOString();

    this.$addEntityDuidPendingDeletion(doc.duid);
    const backendAction = this.$backend.doc.update({ duid: doc.duid, inTrash: true });
    const action = (async () => {
      await backendAction;
      this.$removeEntityDuidPendingDeletion(doc.duid);
    })();

    if (awaitBackend) {
      await action;
    }
  },
  $updateDocEditorsAndSubscribersNoBackend(doc, userDuids) {
    const currentEditorDuids = new Set(doc.editorDuids);
    const newEditorDuids = userDuids.filter((e) => !currentEditorDuids.has(e));
    // eslint-disable-next-line no-param-reassign
    doc.editorDuids = [...currentEditorDuids, ...newEditorDuids];

    const currentSubscriberDuids = new Set(doc.subscriberDuids);
    const newSubscriberDuids = userDuids.filter((e) => !currentSubscriberDuids.has(e));
    // eslint-disable-next-line no-param-reassign
    doc.subscriberDuids = [...currentSubscriberDuids, ...newSubscriberDuids];

    return { newEditorDuids, newSubscriberDuids };
  },
  $createOrUpdateDoc(doc, extras = {}) {
    const { hardUpdate = false } = extras;

    const currentDoc = this.getDocByDuidIfLoaded(doc.duid);
    if (this.$isEntityDuidPendingDeletion(doc.duid)) {
      return;
    }

    // TODO remove this check when we are filtering out spurious tasks on BE of WS
    if (!this.getFolderByDuid(doc.folderDuid)) {
      if (currentDoc) {
        this.$deleteDoc(doc);
      }
      return;
    }

    // eslint-disable-next-line no-param-reassign
    doc.pageKind = PageKind.DOC;
    // eslint-disable-next-line no-param-reassign
    doc.kind = DocKind.DOC;

    if (!currentDoc) {
      this._duidsToDocs.set(doc.duid, doc);
    } else {
      Object.assign(currentDoc, doc);
    }

    this.$unblacklistEntityDuid(doc.duid);
    this.$addEntitiesToPaginationStates(doc.folderDuid, [doc]);
    updateInSorted(this._duidsToDocs, this._sortedDocDuids, doc.duid, this.$getDocSortingFn(FolderSort.ORDER));

    if (hardUpdate) {
      this.$useAppStore().doc?.hardUpdateText();
    }
  },
  $deleteDoc(doc) {
    this._duidsToDocs.delete(doc.duid);
    removeFromSorted(this._sortedDocDuids, doc.duid);
    this.$removeEntitiesFromPaginationStates([doc]);
    this.$useAppStore().removeRecentDoc(doc.duid);
  },
  /* TASK-DOC RELATIONSHIPS */
  async createTaskDocRelationship(taskDuid, docDuid, options = {}) {
    const { awaitBackend = false } = options;
    const task = this.getTaskByDuidIfLoaded(taskDuid);
    const doc = this.getDocByDuidIfLoaded(docDuid);
    if (!task || !doc) {
      return undefined;
    }

    const existingRelationship =
      task.docRelationships.find((e) => e.docDuid === docDuid) ||
      doc.taskRelationships.find((e) => e.taskDuid === taskDuid);
    if (existingRelationship) {
      return existingRelationship;
    }

    const newTaskDocRelationship: TaskDocRelationship = {
      duid: makeDuid(),
      taskDuid,
      docDuid,
    };

    this.$createTaskDocRelationship(newTaskDocRelationship);

    const backendAction = this.$backend.taskDocRelationship.create(newTaskDocRelationship);
    if (awaitBackend) {
      await backendAction;
    }

    return newTaskDocRelationship;
  },
  async deleteTaskDocRelationship(taskDuid, docDuid, options = {}) {
    const { awaitBackend = false } = options;
    const appStore = this.$useAppStore();
    const task = this.getTaskByDuidIfLoaded(taskDuid);
    const doc = this.getDocByDuidIfLoaded(docDuid);
    if (!task || !doc) {
      return;
    }
    const taskDocRelationship =
      task.docRelationships.find((e) => e.docDuid === docDuid) ||
      doc.taskRelationships.find((e) => e.taskDuid === taskDuid);
    if (!taskDocRelationship) {
      return;
    }

    const now = new Date().toISOString();
    const updaterDuid = this.$useUserStore().duid;
    task.docRelationships = task.docRelationships.filter((e) => e.duid !== taskDocRelationship.duid);
    task.updatedAt = now;
    task.updatedByDuid = updaterDuid;
    doc.taskRelationships = doc.taskRelationships.filter((e) => e.duid !== taskDocRelationship.duid);
    doc.updatedAt = now;
    doc.updatedByDuid = updaterDuid;

    appStore.addRecentTask(task);
    appStore.addRecentDoc(doc);

    const backendAction = this.$backend.taskDocRelationship.delete(taskDocRelationship.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async $createTaskDocRelationship(taskDocRelationship) {
    const task = await this.getTaskByDuid(taskDocRelationship.taskDuid);
    const doc = await this.getDocByDuid(taskDocRelationship.docDuid);
    // TODO remove this check when we are filtering out spurious updates on BE of WS
    if (!task || !doc) {
      return;
    }

    // TODO unclear why reactive is needed here. should figure it out and remove
    const relationship = reactive(taskDocRelationship);
    task.docRelationships.push(relationship);
    task.updatedAt = new Date().toISOString();
    task.updatedByDuid = this.$useUserStore().duid;

    doc.taskRelationships.push(taskDocRelationship);
    doc.updatedAt = new Date().toISOString();
    doc.updatedByDuid = this.$useUserStore().duid;
  },
};

export { actions, getters };
